import React, { useEffect, useMemo, useState } from "react";
import AuthImg from "@/components/AuthImg";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2, UsersRound, Hand, CheckCircle2, Clock, Info } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember"];

function fmtDt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch (_e) { return "—"; }
}

export function TeamPerformanceContent() {
  const { accessToken } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 0 = ganzes Jahr
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/admin/billing/team-performance", { params: { year, month } })
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) toast.error(apiError(e, "Team-Leistung konnte nicht geladen werden")); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [year, month]);

  const years = useMemo(() => {
    const y = now.getFullYear();
    return [y - 1, y, y + 1];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = data?.totals || {};

  return (
    <div className="space-y-5" data-testid="team-performance">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-sky-700">Benutzer</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900 mt-1">Team-Leistung</h1>
          <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
            Akten-Counter pro Mitarbeiter — wer hat wie viele Akten übernommen und abgewickelt.
            Basis für die spätere <b>Vergütung pro Akte</b>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-[150px] h-9" data-testid="team-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Ganzes Jahr</SelectItem>
              {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-[100px] h-9" data-testid="team-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade…
        </div>
      ) : (
        <>
          {/* Totale */}
          <div className="grid grid-cols-3 gap-3">
            <TotalCard icon={<Hand className="h-4 w-4" />} label="Übernommen" value={t.claimed ?? 0} tone="text-violet-700" />
            <TotalCard icon={<CheckCircle2 className="h-4 w-4" />} label="Abgewickelt" value={t.completed ?? 0} tone="text-emerald-700" />
            <TotalCard icon={<Clock className="h-4 w-4" />} label="Offen zugewiesen" value={t.open ?? 0} tone="text-amber-700" />
          </div>

          {/* Mitarbeiter-Karten */}
          {(data?.members || []).length === 0 ? (
            <div className="rounded-2xl border border-zinc-200 bg-white py-12 text-center text-zinc-400" data-testid="team-empty">
              <UsersRound className="h-8 w-8 mx-auto mb-2 text-zinc-300" />
              Noch keine übernommenen Akten im Zeitraum.
              <div className="text-xs mt-1">Mitarbeiter übernehmen Akten im Bereich „Akten-Live" per Klick auf „Übernehmen".</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {data.members.map((m) => (
                <div key={m.user_id} className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid={`team-member-${m.email}`}>
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-full overflow-hidden bg-zinc-100 flex items-center justify-center text-sm font-semibold text-zinc-600 shrink-0">
                      {m.avatar_path
                        ? <AuthImg path={m.avatar_path} token={accessToken} className="h-full w-full object-cover" alt={m.name} />
                        : (m.name || "?").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-zinc-900 truncate">{m.name}</div>
                      <div className="text-xs text-zinc-500 truncate">{m.email}</div>
                    </div>
                    <Badge variant="outline" className="ml-auto shrink-0 bg-violet-50 text-violet-700 border-violet-200 text-[10px] uppercase">
                      {m.role === "mitarbeiter" ? "Mitarbeiter" : m.role}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                    <div className="rounded-xl bg-violet-50/70 py-2.5">
                      <div className="text-xl font-bold text-violet-700" data-testid={`team-claimed-${m.email}`}>{m.claimed}</div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-violet-600/70">Übernommen</div>
                    </div>
                    <div className="rounded-xl bg-emerald-50/70 py-2.5">
                      <div className="text-xl font-bold text-emerald-700" data-testid={`team-completed-${m.email}`}>{m.completed}</div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-600/70">Abgewickelt</div>
                    </div>
                    <div className="rounded-xl bg-amber-50/70 py-2.5">
                      <div className="text-xl font-bold text-amber-700">{m.open}</div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-amber-600/70">Offen</div>
                    </div>
                  </div>
                  <div className="text-xs text-zinc-500 mt-3 text-center">
                    Ø Bearbeitungszeit: <b>{m.avg_hours != null ? `${m.avg_hours} Std.` : "—"}</b>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Zuletzt abgewickelte Akten */}
          {(data?.recent_completed || []).length > 0 && (
            <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.18em] font-bold text-zinc-500">
                Zuletzt abgewickelte Akten
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500">
                    <tr className="border-b border-zinc-200">
                      <th className="text-left px-4 py-2.5">Akte</th>
                      <th className="text-left px-4 py-2.5">Kunde</th>
                      <th className="text-left px-4 py-2.5">Mitarbeiter</th>
                      <th className="text-left px-4 py-2.5">Übernommen</th>
                      <th className="text-left px-4 py-2.5">Abgewickelt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_completed.map((r) => (
                      <tr key={r.case_id} className="border-b border-zinc-100 last:border-0" data-testid={`team-case-${r.case_number}`}>
                        <td className="px-4 py-2.5 font-mono text-xs">{r.case_number || "—"}</td>
                        <td className="px-4 py-2.5 truncate max-w-[180px]">{r.customer_name || "—"}</td>
                        <td className="px-4 py-2.5">{r.assigned_to_name}</td>
                        <td className="px-4 py-2.5 text-xs text-zinc-500">{fmtDt(r.assigned_at)}</td>
                        <td className="px-4 py-2.5 text-xs text-emerald-700 font-medium">{fmtDt(r.review_completed_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex items-start gap-2 rounded-xl bg-sky-50 border border-sky-200 px-3.5 py-2.5 text-xs text-sky-800">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              <b>Vergütung pro Akte folgt:</b> Sobald ihr die Berechnung festgelegt habt, rechnen wir
              die abgewickelten Akten hier direkt in Euro um.
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function TotalCard({ icon, label, value, tone }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white px-4 py-3.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500">
        {icon}{label}
      </div>
      <div className={`text-2xl font-display font-bold mt-1 ${tone}`}>{value}</div>
    </div>
  );
}
